(() => {
  const rootUrl = () => (window.Shopify?.routes?.root || '/');
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const ui = window.VantageHeaderUI || (window.VantageHeaderUI = {});

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const escapeAttr = (value = '') => escapeHtml(value).replace(/`/g, '&#96;');

  const formatMoney = (cents, currency = 'USD') => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency, maximumFractionDigits: 2
      }).format(amount);
    } catch (_) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const updateCartCounts = (count) => {
    document.querySelectorAll('[data-vbh-cart-count]').forEach((node) => { node.textContent = String(count || 0); });
    document.querySelectorAll('[data-vbh-cart-open]').forEach((node) => {
      node.setAttribute('aria-label', `Cart with ${count || 0} items`);
    });
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const elements = [...container.querySelectorAll(focusableSelector)].filter((element) => !element.hidden && element.offsetParent !== null);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const closeActiveDialog = (except) => {
    if (ui.activeDialog && ui.activeDialog !== except && typeof ui.activeDialog.close === 'function') ui.activeDialog.close(false);
  };

  class VantageDialog extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.panel = this.querySelector('[role="dialog"]');
      this.querySelectorAll('[data-vbh-dialog-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        trapFocus(event, this.panel || this);
      });
    }

    show(trigger) {
      closeActiveDialog(this);
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.dataset.open = 'true';
      document.documentElement.classList.add('vbh-lock');
      ui.activeDialog = this;
      requestAnimationFrame(() => this.focusInitial());
    }

    close(restoreFocus = true) {
      if (this.hidden) return;
      this.hidden = true;
      this.dataset.open = 'false';
      document.documentElement.classList.remove('vbh-lock');
      if (ui.activeDialog === this) ui.activeDialog = null;
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((node) => node.setAttribute('aria-expanded', 'false'));
      if (restoreFocus && this.trigger instanceof HTMLElement) this.trigger.focus();
    }

    focusInitial() {
      (this.panel || this).focus();
    }
  }

  class VantageSearchModal extends VantageDialog {
    connectedCallback() {
      super.connectedCallback();
      if (this.dataset.searchReady) return;
      this.dataset.searchReady = 'true';
      this.input = this.querySelector('[data-vbh-search-input]');
      this.form = this.querySelector('[data-vbh-search-form]');
      this.results = this.querySelector('[data-vbh-search-results]');
      this.status = this.querySelector('[data-vbh-search-status]');
      this.clearButton = this.querySelector('[data-vbh-search-clear]');
      this.initialMarkup = this.results?.innerHTML || '';
      this.timer = null;
      this.abortController = null;

      document.querySelectorAll('[data-vbh-search-open]').forEach((trigger) => {
        if (trigger.dataset.vbhBound) return;
        trigger.dataset.vbhBound = 'true';
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          trigger.setAttribute('aria-expanded', 'true');
          this.show(trigger);
        });
      });

      this.input?.addEventListener('input', () => {
        const term = this.input.value.trim();
        this.clearButton.hidden = !term;
        clearTimeout(this.timer);
        if (!term) {
          this.resetResults();
          return;
        }
        this.timer = setTimeout(() => this.fetchResults(term), 220);
      });

      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.clearButton.hidden = true;
        this.resetResults();
        this.input.focus();
      });

      this.input?.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const options = this.resultOptions();
        if (!options.length) return;
        event.preventDefault();
        (event.key === 'ArrowDown' ? options[0] : options[options.length - 1]).focus();
      });

      this.results?.addEventListener('keydown', (event) => {
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const options = this.resultOptions();
        const index = options.indexOf(document.activeElement);
        if (index < 0) return;
        event.preventDefault();
        const next = event.key === 'ArrowDown' ? Math.min(index + 1, options.length - 1) : Math.max(index - 1, 0);
        options[next].focus();
      });
    }

    focusInitial() {
      this.input?.focus();
    }

    resultOptions() {
      return [...(this.results?.querySelectorAll('[data-predictive-option]') || [])];
    }

    resetResults() {
      this.abortController?.abort();
      if (this.results) {
        this.results.innerHTML = this.initialMarkup;
        this.results.removeAttribute('aria-busy');
      }
      this.input?.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
    }

    async fetchResults(term) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.results?.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = 'Loading search suggestions';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,vendor',
        section_id: 'vantage-predictive-search'
      });
      try {
        const response = await fetch(`${rootUrl()}search/suggest?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'text/html' }
        });
        const text = await response.text();
        if (!response.ok) throw new Error(`${response.status}: ${text}`);
        const documentFragment = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = documentFragment.querySelector('#shopify-section-vantage-predictive-search') || documentFragment.querySelector('[id^="shopify-section-"]');
        if (!wrapper) throw new Error('Predictive search section was not returned.');
        if (this.input?.value.trim() !== term) return;
        this.results.innerHTML = wrapper.innerHTML;
        const count = Number(this.results.querySelector('[data-result-count]')?.dataset.resultCount || 0);
        this.input.setAttribute('aria-expanded', 'true');
        if (this.status) this.status.textContent = `${count} search suggestions available`;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Vantage predictive search:', error);
        if (this.results) this.results.innerHTML = `<div class="vps"><div class="vps-empty"><p>SEARCH UNAVAILABLE</p><h3>Use the search button to view all results.</h3><a href="${escapeAttr(this.form?.action || '/search')}?q=${encodeURIComponent(term)}">Search for “${escapeHtml(term)}” <span aria-hidden="true">→</span></a></div></div>`;
        if (this.status) this.status.textContent = 'Predictive search is temporarily unavailable';
      } finally {
        this.results?.removeAttribute('aria-busy');
      }
    }
  }

  class VantageCartDrawer extends VantageDialog {
    connectedCallback() {
      super.connectedCallback();
      if (this.dataset.cartReady) return;
      this.dataset.cartReady = 'true';
      this.lines = this.querySelector('[data-vbh-cart-lines]');
      this.footer = this.querySelector('[data-vbh-cart-footer]');
      this.subtotal = this.querySelector('[data-vbh-cart-subtotal]');
      this.error = this.querySelector('[data-vbh-cart-error]');
      this.currency = document.documentElement.dataset.currency || 'USD';
      this.refreshTimer = null;

      document.querySelectorAll('[data-vbh-cart-open]').forEach((trigger) => {
        if (trigger.dataset.vbhBound) return;
        trigger.dataset.vbhBound = 'true';
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          trigger.setAttribute('aria-expanded', 'true');
          this.open(trigger);
        });
      });

      this.lines?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-vbh-cart-change]');
        if (!button) return;
        this.changeLine(Number(button.dataset.line), Number(button.dataset.quantity));
      });

      const externalRefresh = (event) => {
        if (event?.detail?.source === 'vantage-cart') return;
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => this.refresh(false), 120);
      };
      document.addEventListener('cart:update', externalRefresh);
      document.addEventListener('cart:refresh', externalRefresh);
      document.addEventListener('product:added', externalRefresh);
      document.addEventListener('shopify:cart:lines-update', (event) => {
        Promise.resolve(event.promise).catch(() => null).finally(() => externalRefresh(event));
      });
    }

    focusInitial() {
      this.panel?.focus();
    }

    open(trigger) {
      this.show(trigger);
      this.refresh(true);
    }

    setLoading(loading) {
      if (!this.lines) return;
      this.lines.dataset.loading = loading ? 'true' : 'false';
      this.lines.querySelectorAll('button,input').forEach((control) => { control.disabled = loading; });
    }

    showError(message = '') {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message;
    }

    async fetchCart() {
      const response = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to load the cart.');
      return cart;
    }

    async refresh(showLoading = false) {
      if (showLoading) this.setLoading(true);
      try {
        const cart = await this.fetchCart();
        this.render(cart);
      } catch (error) {
        console.error('Vantage cart drawer:', error);
        this.showError(error.message || 'Unable to load your cart.');
      } finally {
        this.setLoading(false);
      }
    }

    async changeLine(line, quantity) {
      if (!line || Number.isNaN(quantity)) return;
      this.showError('');
      this.setLoading(true);
      try {
        const response = await fetch(`${rootUrl()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: Math.max(0, quantity) })
        });
        const cart = await response.json();
        if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to update the cart.');
        this.render(cart);
        document.dispatchEvent(new CustomEvent('cart:update', { detail: { cart, source: 'vantage-cart' } }));
      } catch (error) {
        console.error('Vantage cart change:', error);
        this.showError(error.message || 'Unable to update this item.');
        await this.refresh(false);
      } finally {
        this.setLoading(false);
      }
    }

    render(cart) {
      this.currency = cart.currency || this.currency;
      updateCartCounts(cart.item_count);
      this.showError('');
      if (!this.lines) return;
      if (!cart.item_count) {
        this.lines.innerHTML = '<div class="vbh-cart-empty"><span>0</span><h3>Your cart is empty.</h3><p>Explore the collection and add products to start planning your space.</p><a href="/collections/all-products">SHOP ALL PRODUCTS <span aria-hidden="true">↗</span></a></div>';
        if (this.footer) this.footer.hidden = true;
        return;
      }
      this.lines.innerHTML = cart.items.map((item, index) => this.itemMarkup(item, index + 1)).join('');
      if (this.footer) this.footer.hidden = false;
      if (this.subtotal) this.subtotal.textContent = formatMoney(cart.total_price, this.currency);
    }

    itemMarkup(item, line) {
      const title = item.product_title || item.title || 'Product';
      const variant = item.product_has_only_default_variant ? '' : (item.variant_title || '');
      const image = item.featured_image?.url || item.image || '';
      const media = image
        ? `<img src="${escapeAttr(image)}" alt="${escapeAttr(item.featured_image?.alt || title)}" loading="lazy" width="180" height="206">`
        : '<svg viewBox="0 0 100 115" aria-hidden="true"><rect width="100" height="115" fill="#f1f1ee" stroke="none"/></svg>';
      return `<article class="vbh-cart-item" data-line="${line}">
        <a class="vbh-cart-media" href="${escapeAttr(item.url || '#')}">${media}</a>
        <div class="vbh-cart-copy">
          <a class="vbh-cart-title" href="${escapeAttr(item.url || '#')}">${escapeHtml(title)}</a>
          ${variant ? `<p>${escapeHtml(variant)}</p>` : ''}
          <div class="vbh-cart-row"><div class="vbh-cart-qty">
            <button type="button" aria-label="Decrease ${escapeAttr(title)} quantity" data-vbh-cart-change data-line="${line}" data-quantity="${Math.max(0, item.quantity - 1)}">−</button>
            <input type="text" value="${Number(item.quantity)}" readonly aria-label="Quantity">
            <button type="button" aria-label="Increase ${escapeAttr(title)} quantity" data-vbh-cart-change data-line="${line}" data-quantity="${Number(item.quantity) + 1}">+</button>
          </div><strong>${formatMoney(item.final_line_price, this.currency)}</strong></div>
          <button class="vbh-cart-remove" type="button" data-vbh-cart-change data-line="${line}" data-quantity="0">REMOVE</button>
        </div>
      </article>`;
    }
  }

  const initHeader = (header) => {
    if (!header || header.dataset.headerReady) return;
    header.dataset.headerReady = 'true';
    const toggle = header.querySelector('[data-vbh-mobile-toggle]');
    const drawer = header.querySelector('[data-vbh-drawer]');
    const shop = header.querySelector('[data-vbh-shop]');
    const shopButton = header.querySelector('[data-vbh-shop-button]');
    const desktop = matchMedia('(min-width:901px)');
    const setMobile = (open) => {
      if (!toggle || !drawer) return;
      toggle.setAttribute('aria-expanded', String(open));
      drawer.hidden = !open;
    };
    const setMega = (open) => {
      if (!shop || !shopButton) return;
      shop.dataset.open = open ? 'true' : 'false';
      shopButton.setAttribute('aria-expanded', String(open));
    };
    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      setMobile(open);
      if (open) setMega(false);
    });
    shopButton?.addEventListener('click', () => { if (desktop.matches) setMega(shopButton.getAttribute('aria-expanded') !== 'true'); });
    shop?.addEventListener('pointerenter', () => { if (desktop.matches) setMega(true); });
    shop?.addEventListener('pointerleave', () => { if (desktop.matches && !shop.matches(':focus-within')) setMega(false); });
    document.addEventListener('click', (event) => { if (!header.contains(event.target)) { setMobile(false); setMega(false); } });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || ui.activeDialog) return;
      const mobileOpen = toggle?.getAttribute('aria-expanded') === 'true';
      const megaOpen = shopButton?.getAttribute('aria-expanded') === 'true';
      setMobile(false); setMega(false);
      if (mobileOpen) toggle?.focus(); else if (megaOpen) shopButton?.focus();
    });
    addEventListener('resize', () => { desktop.matches ? setMobile(false) : setMega(false); }, { passive: true });
  };

  if (!customElements.get('vantage-search-modal')) customElements.define('vantage-search-modal', VantageSearchModal);
  if (!customElements.get('vantage-cart-drawer')) customElements.define('vantage-cart-drawer', VantageCartDrawer);

  const initAll = (root = document) => root.querySelectorAll('[data-vbh]').forEach(initHeader);
  const configureShopifyActions = (attempt = 0) => {
    const action = window.Shopify?.actions?.openCart;
    const drawer = document.querySelector('vantage-cart-drawer');
    if (action?.configure && drawer) {
      if (!ui.openCartConfigured) {
        action.configure({ handler() { drawer.open(document.querySelector('[data-vbh-cart-open]')); } });
        ui.openCartConfigured = true;
      }
      return;
    }
    if (attempt < 20) setTimeout(() => configureShopifyActions(attempt + 1), 150);
  };

  const ready = () => { initAll(document); configureShopifyActions(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready, { once: true }); else ready();
  document.addEventListener('shopify:section:load', (event) => { initAll(event.target); configureShopifyActions(); });
})();
